/**
 * CTE Cycle Simulation PDF Export
 * Generates a professional PDF report from cycle simulation results.
 * Layout helpers live in the shared writer (src/utils/pdf-writer.js);
 * the native temperature/distortion chart is specific to this report.
 */

import {
  createDoc, checkPage, drawHeader, drawSectionTitle, drawKeyValue,
  drawTable, drawStatBox, drawWrappedText, stripMarkdown, drawDisclaimerFooter,
  BRAND_COLOR, TEXT_COLOR, MUTED_COLOR, WHITE, LIGHT_BG, BORDER_COLOR,
  GREEN, AMBER, RED, PAGE_W, MARGIN, CONTENT_W
} from '../../utils/pdf-writer';

const TEMP_COLOR = AMBER;
const DISP_COLOR = BRAND_COLOR;
const CURE_COLOR = GREEN;
const PEAK_COLOR = RED;

function getRiskColor(value) {
  if (value < 0.1) return GREEN;
  if (value < 0.5) return AMBER;
  return RED;
}

function drawChart(doc, y, chartData, cureTime, peakTime) {
  if (!chartData || chartData.length < 2) return y;

  const chartW = CONTENT_W;
  const chartH = 55;
  const padL = 14;
  const padR = 14;
  const padT = 4;
  const padB = 12;
  const plotW = chartW - padL - padR;
  const plotH = chartH - padT - padB;
  const ox = MARGIN + padL;
  const oy = y + padT;

  const maxTime = chartData[chartData.length - 1].time;
  const maxTemp = Math.max(...chartData.map(d => d.temp));
  const maxDisp = Math.max(...chartData.map(d => d.disp));
  const minTemp = Math.min(...chartData.map(d => d.temp));

  const tempRange = (maxTemp - minTemp) || 1;
  const dispRange = maxDisp || 0.001;

  const toX = (t) => ox + (t / maxTime) * plotW;
  const toYTemp = (t) => oy + plotH - ((t - minTemp) / tempRange) * plotH;
  const toYDisp = (d) => oy + plotH - (d / dispRange) * plotH;

  doc.setFillColor(...LIGHT_BG);
  doc.setDrawColor(...BORDER_COLOR);
  doc.roundedRect(MARGIN, y, chartW, chartH, 1.5, 1.5, 'FD');

  doc.setDrawColor(220, 220, 220);
  doc.setLineWidth(0.15);
  for (let i = 1; i <= 4; i++) {
    const gy = oy + (plotH / 5) * i;
    doc.line(ox, gy, ox + plotW, gy);
  }

  if (cureTime !== null && cureTime !== undefined) {
    const cx = toX(parseFloat(cureTime));
    doc.setDrawColor(...CURE_COLOR);
    doc.setLineWidth(0.3);
    doc.setLineDashPattern([1.5, 1.5], 0);
    doc.line(cx, oy, cx, oy + plotH);
    doc.setLineDashPattern([], 0);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(5.5);
    doc.setTextColor(...CURE_COLOR);
    doc.text('Cure', cx, oy - 1, { align: 'center' });
  }

  if (peakTime !== null && peakTime !== undefined) {
    const px = toX(parseFloat(peakTime));
    doc.setDrawColor(...PEAK_COLOR);
    doc.setLineWidth(0.3);
    doc.setLineDashPattern([1.5, 1.5], 0);
    doc.line(px, oy, px, oy + plotH);
    doc.setLineDashPattern([], 0);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(5.5);
    doc.setTextColor(...PEAK_COLOR);
    doc.text('Peak', px, oy - 1, { align: 'center' });
  }

  const step = Math.max(1, Math.floor(chartData.length / 200));

  doc.setDrawColor(...TEMP_COLOR);
  doc.setLineWidth(0.5);
  for (let i = step; i < chartData.length; i += step) {
    const prev = chartData[i - step];
    const curr = chartData[i];
    doc.line(toX(prev.time), toYTemp(prev.temp), toX(curr.time), toYTemp(curr.temp));
  }

  doc.setDrawColor(...DISP_COLOR);
  doc.setLineWidth(0.5);
  for (let i = step; i < chartData.length; i += step) {
    const prev = chartData[i - step];
    const curr = chartData[i];
    doc.line(toX(prev.time), toYDisp(prev.disp), toX(curr.time), toYDisp(curr.disp));
  }

  doc.setLineWidth(0.2);
  doc.setDrawColor(...TEXT_COLOR);
  doc.line(ox, oy + plotH, ox + plotW, oy + plotH);
  doc.line(ox, oy, ox, oy + plotH);
  doc.line(ox + plotW, oy, ox + plotW, oy + plotH);

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(5.5);
  doc.setTextColor(...MUTED_COLOR);

  const nTicks = 5;
  for (let i = 0; i <= nTicks; i++) {
    const t = (maxTime / nTicks) * i;
    const x = toX(t);
    doc.text(t.toFixed(0), x, oy + plotH + 4, { align: 'center' });
  }
  doc.setFontSize(6);
  doc.text('Time (min)', ox + plotW / 2, oy + plotH + 9, { align: 'center' });

  doc.setTextColor(...TEMP_COLOR);
  doc.setFontSize(5.5);
  for (let i = 0; i <= 4; i++) {
    const val = minTemp + (tempRange / 4) * i;
    const ty = toYTemp(val);
    doc.text(val.toFixed(0), ox - 2, ty + 1, { align: 'right' });
  }
  doc.setFontSize(5);
  doc.text('Temp (deg C)', ox - 2, oy - 1, { align: 'right' });

  doc.setTextColor(...DISP_COLOR);
  doc.setFontSize(5.5);
  for (let i = 0; i <= 4; i++) {
    const val = (dispRange / 4) * i;
    const dy = toYDisp(val);
    doc.text(val.toFixed(2), ox + plotW + 2, dy + 1);
  }
  doc.setFontSize(5);
  doc.text('Disp (mm)', ox + plotW + 2, oy - 1);

  const legendY = y + chartH + 3;
  const legendX = MARGIN + chartW / 2 - 25;
  doc.setFillColor(...TEMP_COLOR);
  doc.rect(legendX, legendY, 6, 2, 'F');
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(6);
  doc.setTextColor(...TEXT_COLOR);
  doc.text('Temperature', legendX + 8, legendY + 1.8);

  doc.setFillColor(...DISP_COLOR);
  doc.rect(legendX + 35, legendY, 6, 2, 'F');
  doc.text('Max Distortion', legendX + 43, legendY + 1.8);

  return y + chartH + 10;
}

export function generateCycleSimPDF(data) {
  const doc = createDoc();

  let y = drawHeader(doc, 'Thermal Cycle Simulation Report');

  // --- Project Summary ---
  y = drawSectionTitle(doc, y, 'Project Summary');
  y = drawKeyValue(doc, y, 'Mould Material', data.mouldMaterial || '-');
  if (data.mouldCureTemp !== null && data.mouldCureTemp !== undefined) {
    y = drawKeyValue(doc, y, 'Mould Tg', `${data.mouldCureTemp} deg C`);
  }
  if (data.partMaterial) {
    y = drawKeyValue(doc, y, 'Part Material', data.partMaterial);
    y = drawKeyValue(doc, y, 'Part Cure Temp', `${data.partCureTemp} deg C`);
  }
  y = drawKeyValue(doc, y, 'Total Duration', `${data.totalDuration} min`);
  y += 2;

  // --- Key Stats ---
  y = drawSectionTitle(doc, y, 'Key Results');
  y = checkPage(doc, y, 28);
  const statW = (CONTENT_W - 12) / 4;
  drawStatBox(doc, MARGIN, y, statW, 'Peak Distortion', data.peakDisp, 'mm');
  drawStatBox(doc, MARGIN + statW + 4, y, statW, 'Peak Temp', data.peakTemp, 'deg C');
  drawStatBox(doc, MARGIN + (statW + 4) * 2, y, statW, 'Peak Time', data.peakTime, 'min');
  drawStatBox(doc, MARGIN + (statW + 4) * 3, y, statW, 'Total Duration', data.totalDuration, 'min');
  y += 28;

  // --- Distortion risk indicator ---
  const riskColor = getRiskColor(parseFloat(data.peakDisp));
  doc.setFillColor(...riskColor);
  doc.roundedRect(MARGIN, y, CONTENT_W, 8, 1.5, 1.5, 'F');
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(8);
  doc.setTextColor(...WHITE);
  const peakVal = parseFloat(data.peakDisp);
  const riskText = peakVal < 0.1
    ? 'LOW RISK — Compensation likely not needed'
    : peakVal < 0.5
      ? 'MODERATE — Compensation recommended for precision work'
      : 'HIGH — Compensation is essential';
  doc.text(riskText, PAGE_W / 2, y + 5.5, { align: 'center' });
  y += 14;

  // --- Cure Point Data ---
  if (data.cureInfo) {
    y = drawSectionTitle(doc, y, 'Part Cure Point Analysis');
    y = drawKeyValue(doc, y, 'Part Material', data.cureInfo.partName);
    y = drawKeyValue(doc, y, 'Cure Temperature', `${data.cureInfo.partCureTemp} deg C`);
    y = drawKeyValue(doc, y, 'Mould Distortion at Cure', `${data.cureInfo.disp} mm`);
    y = drawKeyValue(doc, y, 'Part Cures At', `${data.cureInfo.time} min`);
    y = drawKeyValue(doc, y, 'Extra Movement After Cure', `${data.cureInfo.extraMovement} mm`);
    y += 2;
  }

  // --- Cycle Profile ---
  y = drawSectionTitle(doc, y, 'Cycle Profile');
  y = drawKeyValue(doc, y, 'Start Temperature', `${data.startTemp} deg C`);
  y += 2;

  const stepRows = data.cycleSteps.map((step, i) => {
    if (step.type === 'ramp') {
      return [`${i + 1}`, 'Ramp', `${step.targetTemp} deg C`, `${step.rate} deg C/min`];
    }
    return [`${i + 1}`, 'Hold', `${step.temp} deg C`, `${step.duration} min`];
  });
  y = drawTable(doc, y, ['Step', 'Type', 'Target / Temp', 'Rate / Duration'], stepRows);

  // --- Chart (drawn natively) ---
  if (data.chartData && data.chartData.length > 1) {
    y = drawSectionTitle(doc, y, 'Temperature & Distortion Over Time');
    y = checkPage(doc, y, 72);
    y = drawChart(doc, y, data.chartData, data.cureTime, data.peakTime);
  }

  // --- 3D Viewer Screenshot ---
  if (data.viewerImage) {
    y = drawSectionTitle(doc, y, '3D Distortion Viewer');
    const imgH = 50;
    y = checkPage(doc, y, imgH + 10);
    doc.setFillColor(10, 10, 15);
    doc.roundedRect(MARGIN, y, CONTENT_W, imgH, 1.5, 1.5, 'F');
    doc.addImage(data.viewerImage, 'PNG', MARGIN, y, CONTENT_W, imgH);
    y += imgH + 3;
    doc.setFont('helvetica', 'italic');
    doc.setFontSize(7);
    doc.setTextColor(...MUTED_COLOR);
    doc.text('Peak distortion state -- colour map shows displacement magnitude', PAGE_W / 2, y, { align: 'center' });
    doc.setTextColor(...TEXT_COLOR);
    y += 6;
  }

  // --- AI Analysis ---
  if (data.aiAnalysis) {
    y = drawSectionTitle(doc, y, 'AI Thermal Process Review');
    const plain = stripMarkdown(data.aiAnalysis);
    const paragraphs = plain.split('\n\n').filter(p => p.trim());
    for (const para of paragraphs) {
      y = drawWrappedText(doc, y, para.trim(), 9);
      y += 2;
    }
  }

  drawDisclaimerFooter(doc, y, 'Thermal Cycle Simulation Report');

  // --- Save ---
  const date = new Date().toISOString().split('T')[0];
  const safeMat = (data.mouldMaterial || 'Mould').replace(/[^a-z0-9]/gi, '_');
  doc.save(`RapidFusion_CycleSim_${safeMat}_${date}.pdf`);
}
